import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Ticket, ShoppingBag, ChevronLeft, ChevronRight } from "lucide-react";
import api, { assetUrl } from "@/lib/api";

const LOGO = "/logo_fluo.png";

function isSoldOut(p) {
  return !(p.sizes || []).some((s) => s.stock > 0);
}

function ProductCard({ p, horizontal = false }) {
  const soldOut = isSoldOut(p);
  return (
    <Link
      to={`/product/${p.id}`}
      className={`group fluo-card overflow-hidden block ${
        horizontal ? "flex items-stretch" : ""
      }`}
      data-testid={`product-card-${p.id}`}
    >
      <div
        className={`relative overflow-hidden bg-black flex items-center justify-center ${
          horizontal ? "w-1/2 aspect-[3/4] shrink-0" : "aspect-square"
        }`}
      >
        {p.photos?.[0] ? (
          <img
            src={assetUrl(p.photos[0])}
            alt={p.name}
            className={`w-full h-full transition-transform duration-700 group-hover:scale-105 ${
              horizontal ? "object-cover" : "object-contain"
            }`}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/20 font-display text-sm">
            NO PHOTO
          </div>
        )}
        {soldOut && (
          <span className="absolute top-3 left-3 badge-soldout">Sold Out</span>
        )}
        <span className="absolute bottom-3 right-3 bg-black/70 backdrop-blur px-2 py-1 text-xs uppercase tracking-widest border border-white/10">
          {p.category}
        </span>
      </div>
      <div
        className={`p-5 flex gap-3 ${
          horizontal
            ? "flex-1 flex-col justify-center"
            : "justify-between items-start"
        }`}
      >
        <div>
          <h3 className={`font-display leading-tight ${horizontal ? "text-2xl md:text-3xl" : "text-lg"}`}>
            {p.name}
          </h3>
          <p className={`text-white/40 mt-1 ${horizontal ? "text-sm line-clamp-3 mb-3" : "text-xs line-clamp-2"}`}>
            {p.description}
          </p>
        </div>
        <span className={`font-display text-magenta whitespace-nowrap ${horizontal ? "text-3xl md:text-4xl" : "text-xl"}`}>
          {p.price_chf.toFixed(2)} <span className="text-xs">CHF</span>
        </span>
      </div>
    </Link>
  );
}

function HeroCarousel({ photos, intervalMs = 5000 }) {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef(null);

  useEffect(() => {
    if (photos.length <= 1 || paused) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % photos.length), intervalMs);
    return () => clearInterval(t);
  }, [photos.length, intervalMs, paused]);

  const next = () => setIdx((i) => (i + 1) % photos.length);
  const prev = () => setIdx((i) => (i - 1 + photos.length) % photos.length);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 40) {
      if (dx < 0) next();
      else prev();
    }
    touchStartX.current = null;
  };

  if (!photos.length) {
    return (
      <div className="aspect-[4/5] md:aspect-square bg-gradient-to-br from-magenta/20 via-black to-black border border-white/10 flex items-center justify-center">
        <span className="text-white/30 text-sm uppercase tracking-widest text-center px-6">
          Configure les photos du carrousel depuis l&apos;admin
        </span>
      </div>
    );
  }

  const multiple = photos.length > 1;

  return (
    <div
      className="relative aspect-[4/5] md:aspect-square overflow-hidden border border-magenta/30 bg-black group"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      data-testid="hero-carousel"
    >
      {photos.map((src, i) => (
        <img
          key={i}
          src={assetUrl(src)}
          alt=""
          className="absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-1000"
          style={{ opacity: i === idx ? 1 : 0 }}
        />
      ))}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent pointer-events-none" />

      {multiple && (
        <>
          <button
            type="button"
            aria-label="Image précédente"
            onClick={prev}
            className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/40 hover:bg-magenta hover:text-black backdrop-blur-sm border border-white/20 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10"
            data-testid="carousel-prev"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            type="button"
            aria-label="Image suivante"
            onClick={next}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/40 hover:bg-magenta hover:text-black backdrop-blur-sm border border-white/20 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10"
            data-testid="carousel-next"
          >
            <ChevronRight size={20} />
          </button>
        </>
      )}

      <div className="absolute bottom-5 left-5 flex gap-1.5 z-10">
        {photos.map((_, i) => (
          <button
            key={i}
            type="button"
            aria-label={`Aller à l'image ${i + 1}`}
            onClick={() => setIdx(i)}
            className={`h-1 transition-all cursor-pointer ${
              i === idx ? "w-8 bg-magenta" : "w-4 bg-white/30 hover:bg-white/60"
            }`}
            data-testid={`carousel-dot-${i}`}
          />
        ))}
      </div>
    </div>
  );
}

export default function Home() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [carousel, setCarousel] = useState({ images: [], interval_seconds: 5 });

  useEffect(() => {
    api.post("/track-visit").catch(() => {});
    api
      .get("/products")
      .then((r) => setProducts(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
    api
      .get("/carousel")
      .then((r) => setCarousel(r.data))
      .catch(() => {});
  }, []);

  // Use admin-configured carousel images; fallback to product photos if empty
  const heroPhotos = carousel.images.length
    ? carousel.images
    : Array.from(
        new Set(products.flatMap((p) => p.photos || []).filter(Boolean))
      ).slice(0, 6);

  return (
    <div className="min-h-screen">
      {/* Marquee */}
      <div className="border-b border-white/5 py-3 overflow-hidden">
        <div className="marquee text-xs uppercase tracking-[0.4em] text-magenta">
          {Array.from({ length: 12 }).map((_, i) => (
            <span key={i} className="px-6 whitespace-nowrap">
              ★ Boutique officielle · FLUO Neuchâtel · Édition 2027 · Neuchâtel · Merch limité
            </span>
          ))}
        </div>
      </div>

      {/* HERO */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pt-12 lg:pt-20 pb-16 grid md:grid-cols-2 gap-10 lg:gap-16 items-center">
        <div className="reveal">
          <span className="inline-block uppercase text-xs tracking-[0.4em] text-magenta border-l-2 border-magenta pl-3 mb-6">
            Édition 2027 · Merch officiel
          </span>
          <img src={LOGO} alt="FLUO Neuchâtel" className="w-full max-w-md mb-6" />
          <h1 className="font-display text-4xl md:text-6xl leading-none mb-5 neon-soft">
            Porte le <span className="text-magenta">FLUO</span>
            <br />
            avant la nuit.
          </h1>
          <p className="text-white/60 max-w-xl mb-8 text-sm md:text-base leading-relaxed">
            La sélection officielle de vêtements et accessoires <span className="whitespace-nowrap">FLUO&nbsp;Neuchâtel</span>.
            <br />
            Pièces en quantité limitée, livraison en Suisse, paiement&nbsp;sécurisé.
          </p>
          <div className="flex flex-wrap gap-3">
            <a href="#shop" className="btn-fluo" data-testid="hero-shop-btn">
              <ShoppingBag size={18} /> Voir la collection
            </a>
            <a
              href="https://www.fluoneuchatel.com/billetterie"
              target="_blank"
              rel="noreferrer"
              className="btn-ghost"
              data-testid="hero-tickets-btn"
            >
              <Ticket size={16} /> Acheter mon billet
            </a>
          </div>
        </div>

        <div className="reveal" style={{ animationDelay: "0.2s" }}>
          <HeroCarousel
            photos={heroPhotos}
            intervalMs={(carousel.interval_seconds || 5) * 1000}
          />
        </div>
      </section>

      {/* SHOP GRID */}
      <section id="shop" className="max-w-7xl mx-auto px-5 lg:px-8 py-12">
        <div className="flex justify-between items-end mb-10 border-b border-white/10 pb-6">
          <div>
            <span className="text-xs uppercase tracking-widest text-magenta">
              Collection 26
            </span>
            <h2 className="font-display text-3xl md:text-5xl mt-1">La boutique</h2>
          </div>
          <span className="text-white/40 text-sm hidden md:block">
            {products.length} {products.length > 1 ? "produits" : "produit"}
          </span>
        </div>

        {loading ? (
          <div className="text-white/40 text-center py-20">Chargement…</div>
        ) : products.length === 0 ? (
          <div
            className="text-center py-20 border border-dashed border-white/10"
            data-testid="empty-shop"
          >
            <p className="text-white/40 mb-2">
              Aucun produit pour le moment.
            </p>
            <p className="text-xs text-white/30">
              L&apos;administrateur peut en ajouter depuis l&apos;espace admin.
            </p>
          </div>
        ) : (
          <div
            className={`grid gap-5 lg:gap-6 ${
              products.length === 1
                ? "grid-cols-1"
                : "grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
            }`}
            data-testid="shop-grid"
          >
            {products.map((p) => (
              <ProductCard key={p.id} p={p} horizontal={products.length === 1} />
            ))}
          </div>
        )}
      </section>

      {/* TICKET CTA */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 my-20">
        <div className="border border-magenta/40 bg-gradient-to-br from-magenta/10 via-black to-black md:p-0 relative overflow-hidden grid md:grid-cols-2">
          <div className="p-10 md:p-14 lg:p-16 relative z-10 order-2 md:order-1">
            <span className="uppercase tracking-widest text-xs text-magenta">
              Édition 2027 · Neuchâtel
            </span>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl mt-3 leading-none">
              Pas encore <br />
              ton <span className="text-magenta">billet&nbsp;?</span>
            </h2>
            <p className="text-white/60 mt-5 mb-7 max-w-md">
              Tiakola, Jok&apos;air, Leto, RK, Freeze Corleone, TK&nbsp;!
              <br />
              Quantité limitée — ne traîne pas.
            </p>
            <a
              href="https://www.fluoneuchatel.com/billetterie"
              target="_blank"
              rel="noreferrer"
              className="btn-fluo"
              data-testid="cta-billetterie"
            >
              <Ticket size={18} /> Billetterie officielle <ArrowRight size={16} />
            </a>
          </div>
          <div className="relative order-1 md:order-2 min-h-[280px] md:min-h-0">
            <img
              src="https://customer-assets.emergentagent.com/job_event-sales-portal-1/artifacts/zgzwzsnw_image.png"
              alt="Artistes FLUO Neuchâtel 2027"
              className="absolute inset-0 w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r md:from-black/80 md:via-black/20 md:to-transparent from-black/40 to-black/10 md:bg-gradient-to-l pointer-events-none" />
            <div className="absolute -left-10 -top-10 w-60 h-60 bg-magenta/30 rounded-full blur-3xl pulse-glow pointer-events-none" />
          </div>
        </div>
      </section>
    </div>
  );
}
